import React, { forwardRef, useEffect, useState } from 'react'
import _ from 'lodash'
import clsx from 'clsx'
import countDecimals from '../../utils/countDecimals'
import Icon from '../Icon'

const UncontrolledQuantitySelector = forwardRef(function UncontrolledQuantitySelector(props, ref) {

	const {
        className,
		inputRef,
        value,
        onChange,
        min,
        max,
        step,
        debouncingTime,
        disabled,
        format,
        ...other
	} = props

    // Same contract as the controlled variant: formatted while blurred, raw while typing.
    const [isFocused, setIsFocused] = useState(false)

    const minValueCheckingDebounced = _.debounce((numberToCheck) => {
		if (Number(numberToCheck) < min) {
            handleChange(`${min}`)
        }
	}, debouncingTime)

    useEffect(() => {
        minValueCheckingDebounced(value)
        return minValueCheckingDebounced.cancel
    }, [value])

    const handleFocus = (e) => {
        setIsFocused(true)
        const input = e.target
        // The displayed text changes on focus when `format` is set (formatted → raw), so select
        // after that re-render or the selection would land on the text being replaced.
        format ? requestAnimationFrame(() => input.select()) : input.select()
    }

    const handleBlur = () => setIsFocused(false)

    const displayValue = format && !isFocused && `${value}` !== '' ? format(Number(value)) : value

    const handleChange = (inputValue) => {
        inputValue = inputValue.replace(',', '.')
        if (inputValue === '') {
            onChange(inputValue)
            return
        }

        if (Number(inputValue) > max) {
            inputValue = `${max}`
        }
        onChange(inputValue)
    }

    const handleClickSusbstract = () => {
        const newValue = (Number(value) - step).toFixed(countDecimals(step))
        if (newValue < min) {
            onChange(min)
            return
        }

        onChange(newValue)
    }

    const handleClickAdd = () => {
        const newValue = (Number(value) + step).toFixed(countDecimals(step))
        if (newValue > max) {
            onChange(max)
            return
        }

        onChange(newValue)
    }

	return (
		<div ref={ref} className={clsx('Vb-qty-selector', className)} {...other}>
            <button
                className="Vb-qty-selector__btn-substract"
                onClick={handleClickSusbstract}
                disabled={disabled || Number(value) <= min}
            >
				<Icon size={0.75}>minus</Icon>
			</button>
			<input
				ref={inputRef}
				type="text"
                value={displayValue}
                disabled={disabled}
                onChange={(e) => handleChange(e.target.value)}
                onFocus={handleFocus}
                onBlur={handleBlur}
                onKeyDown={(e) => {
                    // Allways allow deletion
					if (['Delete', 'Backspace'].includes(e.key)) {
						return
					}

                    // Restrict more than one decimal separator
					if (/[,.]/.test(e.key) && /[,.]/.test(e.target.value)) {
						e.preventDefault()
						return
					}

                    // Restrict decimal separator at the beggining
					if (/[,.]/.test(e.key) && !`${value}`.length) {
						e.preventDefault()
						return
					}

                    // Restrict more decimals than allowed by step prop
					if (countDecimals(step) && countDecimals(+e.target.value.replace(',', '.')) >= countDecimals(step) && e.target.selectionStart === e.target.selectionEnd) {
						e.preventDefault()
						return
					}
                }}
			/>
            <button
                className="Vb-qty-selector__btn-add"
                onClick={handleClickAdd}
                disabled={disabled || Number(value) >= max}
            >
				<Icon size={0.75}>plus</Icon>
			</button>
		</div>
	)

})

export default UncontrolledQuantitySelector